import { useState } from 'react';
import { Alert, Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/errors';
import { resellerAdminApi } from '@/api/resellerAdmin';
import { useCanWrite } from '@/auth/authHooks';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ViewOnlyTooltip } from '@/components/ViewOnly';
import { useActionError } from '@/components/useActionError';
import { useToast } from '@/components/useToast';
import { useTenant, useTenantReload } from '@/tenant/useTenant';

export function AiDefaultToggle() {
  const { t } = useTranslation();
  const tenant = useTenant();
  const reloadTenant = useTenantReload();
  const canWrite = useCanWrite();
  const describe = useActionError();
  const showToast = useToast();
  const checked = tenant.aiEnabledDefault;
  const [confirm, setConfirm] = useState<'on' | 'off' | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);

  async function handleConfirm() {
    if (confirm === null) return;
    const next = confirm === 'on';
    setInlineError(null);
    try {
      await resellerAdminApi.patchTenant(tenant.slug, { aiEnabledDefault: next });
      await reloadTenant();
      showToast(t(next ? 'settings.ai.toast.on' : 'settings.ai.toast.off'));
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        // RESELLER_ROLE_FORBIDDEN: the handler re-GETs /me. The dialog closes; the page says so.
        setInlineError(describe(err).message);
        return;
      }
      throw err;
    }
  }

  const toggle = (
    <Switch
      checked={checked}
      disabled={!canWrite}
      onChange={(_, value) => setConfirm(value ? 'on' : 'off')}
      slotProps={{ input: { 'aria-label': t('settings.ai.defaultLabel') } }}
    />
  );

  return (
    <Box>
      {inlineError ? (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {inlineError}
        </Alert>
      ) : null}
      {canWrite ? (
        <FormControlLabel control={toggle} label={t('settings.ai.defaultLabel')} />
      ) : (
        <ViewOnlyTooltip>
          <FormControlLabel control={toggle} label={t('settings.ai.defaultLabel')} />
        </ViewOnlyTooltip>
      )}
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
        {t('settings.ai.defaultHelp')}
      </Typography>
      <ConfirmDialog
        open={confirm !== null}
        title={confirm === 'on' ? t('settings.ai.confirmOnTitle') : t('settings.ai.confirmOffTitle')}
        consequence={confirm === 'on' ? t('settings.ai.confirmOn') : t('settings.ai.confirmOff')}
        confirmLabel={confirm === 'on' ? t('settings.ai.turnOn') : t('settings.ai.turnOff')}
        onConfirm={handleConfirm}
        onCancel={() => setConfirm(null)}
        errorMessage={(err) => describe(err).message}
      />
    </Box>
  );
}
